"use client";

import { Marquee } from "./_components/marquee";
import { FaWhatsapp } from "react-icons/fa";
import Image from "next/image";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_API_KEY;

export const LocKudus = ({ background }: { background: boolean }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const { ref: refContainer, inView: inViewContainer } = useInView({
    triggerOnce: true,
    threshold: 0.4,
  });

  const { ref: refStarDesktop, inView: inViewStarDesktop } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { ref: refStarMobile, inView: inViewStarMobile } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (!mapContainer.current) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [110.83554985362163, -6.77876088076202],
      zoom: 15,
    });

    map.on("load", () => {
      new mapboxgl.Marker({ color: "red" })
        .setLngLat([110.83554985362163, -6.77876088076202])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setText("Natura Dental Center")
        )
        .addTo(map)
        .getElement()
        .addEventListener("click", () => {
          window.open("https://maps.app.goo.gl/mCjyVaZAng6kF9Ty9", "_blank");
        });
    });

    return () => map.remove();
  }, []);

  return (
    <div
      ref={refContainer}
      className={`relative w-full h-fit ${background ? "bg-natura-lighter-2" : "bg-transparent"}`}
    >
      {/* Decoration */}
      <div className="w-full h-full absolute inset-0 pointer-events-none">
        <div className="relative w-full h-full">
          <svg
            className="absolute top-[-220px] right-[5vw] sm:block hidden z-50"
            width="116"
            height="316"
            viewBox="0 0 116 316"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              className="transition-all duration-[1.5s] ease-out -z-10"
              opacity="0.35"
              x="8"
              y="8"
              width="100"
              height="300"
              rx="50"
              stroke="#FFBD58"
              strokeWidth="15"
              strokeDasharray={"300%"}
              strokeDashoffset={inViewContainer ? "0%" : "300%"}
            />
          </svg>
          <svg
            ref={refStarDesktop}
            className={`absolute size-[350px] bottom-[0px] right-[0vw] hidden sm:block transition-all duration-[2s] ease-out pb-16 pt-24 z-50 ${inViewStarDesktop ? "scale-100" : "scale-0 z-50"}
            `}
            viewBox="0 0 175 175"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.35"
              d="M87.5 0L98.2177 49.9107C101.109 63.3742 111.626 73.8912 125.089 76.7823L175 87.5L125.089 98.2177C111.626 101.109 101.109 111.626 98.2177 125.089L87.5 175L76.7823 125.089C73.8912 111.626 63.3742 101.109 49.9107 98.2177L0 87.5L49.9107 76.7823C63.3741 73.8912 73.8912 63.3741 76.7823 49.9107L87.5 0Z"
              fill="#FFBD58"
            />
          </svg>
          <svg
            ref={refStarMobile}
            className={`absolute size-[250px] top-[-100px] right-[-18vw] sm:hidden block transition-all duration-[2s] ease-out pb-16 pt-24 z-50 ${inViewStarMobile ? "scale-100" : "scale-0"}
            `}
            viewBox="0 0 175 175"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.35"
              d="M87.5 0L98.2177 49.9107C101.109 63.3742 111.626 73.8912 125.089 76.7823L175 87.5L125.089 98.2177C111.626 101.109 101.109 111.626 98.2177 125.089L87.5 175L76.7823 125.089C73.8912 111.626 63.3742 101.109 49.9107 98.2177L0 87.5L49.9107 76.7823C63.3741 73.8912 73.8912 63.3741 76.7823 49.9107L87.5 0Z"
              fill="#FFBD58"
            />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto h-full px-6 py-12 lg:px-0 lg:pt-14 lg:pb-28 gap-12 sm:gap-16 lg:gap-20 flex lg:flex-row flex-col-reverse items-center justify-center">
        {/* Left content */}
        <div className="flex flex-col gap-3 overflow-hidden col-span-1 items-center w-fit">
          <div className="relative max-w-[327px] sm:max-w-full w-full h-[260px] sm:h-[400px] overflow-hidden rounded-2xl shadow-lg z-50">
            {/* Change with map */}
            <div ref={mapContainer} className="w-full h-full z-40" />
          </div>
          <div className="max-w-[327px] sm:max-w-[500px] z-50">
            <Marquee stringImages={"image_natura_kudus"} />
          </div>
        </div>

        {/* Right content */}
        <div className="flex flex-col gap-6 col-span-1 justify-center w-fit lg:w-[600px] max-w-[327px] sm:max-w-[640px]">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <h4 className="text-natura-darker-1 font-serifText text-base md:text-xl">
                Klinik Cabang
              </h4>
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-natura-primary font-serifText">
                Natura Dental Center Kudus
              </h2>
            </div>
            <div className="flex flex-col gap-1">
              {data.map((item, idx) => (
                <div key={idx} className="flex gap-2.5 items-center">
                  <div
                    className={`relative size-6 md:size-10 flex items-center justify-center bg-natura-primary rounded-full p-1.5 md:p-2.5 transition-all duration-500 ease-out
                      ${inViewContainer ? "scale-100 opacity-100" : "scale-0 opacity-50"}
                    `}
                    style={{
                      transitionDelay: `${0.1 * (idx + 1)}s`,
                    }}
                  >
                    {item.icon}
                  </div>
                  {item.description}
                </div>
              ))}
            </div>
          </div>
          <button
            onClick={() => window.open("https://wa.me/6282261502500", "_blank")}
            className="flex items-center justify-center px-4 md:px-6 py-2 rounded-full bg-natura-primary hover:bg-natura-hover active:bg-natura-pressed w-fit font-nunito text-white font-normal cursor-pointer gap-2 z-50"
          >
            <div className="size-6 sm:size-8 relative">
              <Image
                src={"/hero/whatsapp-color.png"}
                alt="cta-icon"
                fill
                className="object-contain"
              />
            </div>
            <p className=" text-xs sm:text-sm md:text-base">
              Hubungi Natura Kudus
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

const data = [
  {
    icon: (
      <svg
        className="stroke-[2px] stroke-natura-white"
        width="22"
        height="28"
        viewBox="0 0 22 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11 15.25C13.0711 15.25 14.75 13.5711 14.75 11.5C14.75 9.42893 13.0711 7.75 11 7.75C8.92893 7.75 7.25 9.42893 7.25 11.5C7.25 13.5711 8.92893 15.25 11 15.25Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11 1.5C8.34784 1.5 5.8043 2.55357 3.92893 4.42893C2.05357 6.3043 1 8.84784 1 11.5C1 13.865 1.5025 15.4125 2.875 17.125L11 26.5L19.125 17.125C20.4975 15.4125 21 13.865 21 11.5C21 8.84784 19.9464 6.3043 18.0711 4.42893C16.1957 2.55357 13.6522 1.5 11 1.5Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    description: (
      <p className="font-nunito text-sm md:text-base lg:text-lg text-natura-darker-1 max-w-[400px] py-2">
        Jl. Sukun Raya, Penganjaran, Peganjaran, Kecamatan Bae, Kabupaten Kudus,
        Jawa Tengah 59327
      </p>
    ),
  },
  {
    icon: (
      <svg
        className="stroke-[2px] stroke-natura-white"
        width="22"
        height="22"
        viewBox="0 0 22 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 11C1 12.3132 1.25866 13.6136 1.7612 14.8268C2.26375 16.0401 3.00035 17.1425 3.92893 18.0711C4.85752 18.9997 5.95991 19.7363 7.17317 20.2388C8.38642 20.7413 9.68678 21 11 21C12.3132 21 13.6136 20.7413 14.8268 20.2388C16.0401 19.7363 17.1425 18.9997 18.0711 18.0711C18.9997 17.1425 19.7363 16.0401 20.2388 14.8268C20.7413 13.6136 21 12.3132 21 11C21 8.34784 19.9464 5.8043 18.0711 3.92893C16.1957 2.05357 13.6522 1 11 1C8.34784 1 5.8043 2.05357 3.92893 3.92893C2.05357 5.8043 1 8.34784 1 11Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11 5.44482V11.0004L14.3333 14.3337"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    description: (
      <p className="font-nunito text-sm md:text-base lg:text-lg text-natura-darker-1 max-w-[400px] py-2">
        Senin – Minggu: 09.00 – 20.30 WIB <br />
        (Tanggal Merah Tetap Buka)
      </p>
    ),
  },
  {
    icon: (
      <svg
        className="stroke-[2px] stroke-natura-white"
        width="22"
        height="18"
        viewBox="0 0 22 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 3.22222C1 2.63285 1.23413 2.06762 1.65087 1.65087C2.06762 1.23413 2.63285 1 3.22222 1H18.7778C19.3671 1 19.9324 1.23413 20.3491 1.65087C20.7659 2.06762 21 2.63285 21 3.22222V14.3333C21 14.9227 20.7659 15.4879 20.3491 15.9047C19.9324 16.3214 19.3671 16.5556 18.7778 16.5556H3.22222C2.63285 16.5556 2.06762 16.3214 1.65087 15.9047C1.23413 15.4879 1 14.9227 1 14.3333V3.22222Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1 3.22217L11 9.88883L21 3.22217"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    description: (
      <p className="font-nunito text-sm md:text-base lg:text-lg text-natura-darker-1 max-w-[400px] py-2">
        naturadentalkudus@gmail.com
      </p>
    ),
  },
  {
    icon: <FaWhatsapp className="w-full h-full text-natura-white" />,
    description: (
      <p className="font-nunito text-sm md:text-base lg:text-lg text-natura-darker-1 max-w-[400px] py-2">
        +62 822-6150-2500
      </p>
    ),
  },
];
